import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assetAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { formatIndianCurrency } from '../../lib/utils';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import {
    ArrowLeft,
    Monitor,
    Keyboard,
    Smartphone,
    Wifi,
    Package,
    HardDrive,
    Calendar,
    DollarSign,
    User,
    Tag,
    Loader2,
    History,
    Wrench,
    AlertTriangle,
} from 'lucide-react';

const STATUS_COLORS = {
    AVAILABLE: 'bg-green-500',
    ASSIGNED: 'bg-blue-500',
    IN_REPAIR: 'bg-yellow-500',
    RETIRED: 'bg-gray-500',
    LOST: 'bg-red-500',
    DISPOSED: 'bg-gray-400',
};

const ICON_MAP = { Monitor, Keyboard, Smartphone, Wifi, Package, HardDrive };

function AssetDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [asset, setAsset] = useState(null);
    const [history, setHistory] = useState([]);
    const [maintenanceHistory, setMaintenanceHistory] = useState([]);
    const [company, setCompany] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [assetRes, companyRes] = await Promise.all([
                    assetAPI.getAsset(id),
                    companyAPI.getCurrent(),
                ]);
                setAsset(assetRes.data);
                setCompany(companyRes.data);

                // Fetch history
                try {
                    const historyRes = await assetAPI.getHistory(id);
                    setHistory(historyRes.data || []);
                } catch (e) {
                    console.log('No history available');
                }

                // Fetch maintenance
                try {
                    const maintRes = await assetAPI.getMaintenanceHistory(id);
                    setMaintenanceHistory(maintRes.data || []);
                } catch (e) {
                    console.log('No maintenance history');
                }
            } catch (error) {
                console.error('Error fetching asset:', error);
                toast.error('Failed to load asset details');
                navigate('/assets');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (company && !company.modules?.assets) {
        return <ModuleDisabled moduleName="IT Asset Management" />;
    }

    if (!asset) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <AlertTriangle className="h-12 w-12 text-yellow-500" />
                <h2 className="text-xl font-semibold">Asset Not Found</h2>
                <Button onClick={() => navigate('/assets')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Assets
                </Button>
            </div>
        );
    }

    const getStatusBadge = (status) => {
        const colorClass = STATUS_COLORS[status] || 'bg-gray-500';
        return (
            <Badge className={`${colorClass} text-white`}>
                {status?.replace('_', ' ')}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getWarrantyStatus = () => {
        if (!asset.warranty_expiry) return null;
        const expiry = new Date(asset.warranty_expiry);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
            return <Badge variant="destructive">Warranty Expired</Badge>;
        } else if (daysUntilExpiry <= 30) {
            return <Badge className="bg-yellow-500 text-white">Expiring in {daysUntilExpiry} days</Badge>;
        }
        return <Badge variant="outline" className="text-green-600 border-green-600">Under Warranty</Badge>;
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/assets')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold">{asset.name}</h1>
                            {getStatusBadge(asset.status)}
                        </div>
                        <p className="text-muted-foreground">Asset Tag: {asset.asset_tag}</p>
                    </div>
                </div>
                <Button onClick={() => navigate('/assets')}>
                    Back to Assets
                </Button>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Basic Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Tag className="h-5 w-5" />
                            Basic Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Asset Tag</span>
                            <span className="font-medium">{asset.asset_tag}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Category</span>
                            <Badge variant="secondary">{asset.category}</Badge>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Condition</span>
                            <span className="font-medium">{asset.condition || '-'}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Serial Number</span>
                            <span className="font-medium">{asset.serial_number || '-'}</span>
                        </div>
                        {asset.description && (
                            <>
                                <Separator />
                                <div>
                                    <span className="text-muted-foreground">Description</span>
                                    <p className="mt-1">{asset.description}</p>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Purchase & Warranty Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <DollarSign className="h-5 w-5" />
                            Purchase & Warranty
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Purchase Date</span>
                            <span className="font-medium">{formatDate(asset.purchase_date)}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Purchase Price</span>
                            <span className="font-medium">
                                {asset.purchase_price ? formatIndianCurrency(asset.purchase_price) : '-'}
                            </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Warranty Expiry</span>
                            <span className="font-medium">{formatDate(asset.warranty_expiry)}</span>
                        </div>
                        {asset.warranty_expiry && (
                            <>
                                <Separator />
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Warranty Status</span>
                                    {getWarrantyStatus()}
                                </div>
                            </>
                        )}
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Vendor</span>
                            <span className="font-medium">
                                {asset.vendor?.name || '-'}
                            </span>
                        </div>
                        {asset.vendor?.contact_person && (
                            <>
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Contact Person</span>
                                    <span className="font-medium">{asset.vendor.contact_person}</span>
                                </div>
                            </>
                        )}
                        {asset.vendor?.email && (
                            <>
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Vendor Email</span>
                                    <span className="font-medium">{asset.vendor.email}</span>
                                </div>
                            </>
                        )}
                        {asset.vendor?.phone && (
                            <>
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Vendor Phone</span>
                                    <span className="font-medium">{asset.vendor.phone}</span>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Assignment Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <User className="h-5 w-5" />
                            Assignment Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Assigned To</span>
                            <span className="font-medium">{asset.assigned_to_name || 'Not Assigned'}</span>
                        </div>
                        {asset.assigned_to_code && (
                            <>
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Employee Code</span>
                                    <span className="font-medium">{asset.assigned_to_code}</span>
                                </div>
                            </>
                        )}
                        {asset.assigned_date && (
                            <>
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Assigned Date</span>
                                    <span className="font-medium">{formatDate(asset.assigned_date)}</span>
                                </div>
                            </>
                        )}
                        <Separator />
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Location</span>
                            <span className="font-medium">{asset.location || '-'}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* History Section */}
            <div className="grid gap-6 md:grid-cols-2">
                {/* Assignment History */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <History className="h-5 w-5" />
                            Assignment History
                        </CardTitle>
                        <CardDescription>Track of all asset assignments</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {history.length === 0 ? (
                            <p className="text-muted-foreground text-center py-4">No assignment history</p>
                        ) : (
                            <div className="space-y-3">
                                {history.slice(0, 5).map((h, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-2 rounded bg-muted/50">
                                        <div>
                                            <p className="font-medium">{h.action}</p>
                                            <p className="text-sm text-muted-foreground">{h.employee_name || '-'}</p>
                                        </div>
                                        <span className="text-sm text-muted-foreground">
                                            {formatDate(h.created_at)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Maintenance History */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Wrench className="h-5 w-5" />
                            Maintenance History
                        </CardTitle>
                        <CardDescription>Record of repairs and maintenance</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {maintenanceHistory.length === 0 ? (
                            <p className="text-muted-foreground text-center py-4">No maintenance records</p>
                        ) : (
                            <div className="space-y-3">
                                {maintenanceHistory.slice(0, 5).map((m, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-2 rounded bg-muted/50">
                                        <div>
                                            <p className="font-medium">{m.maintenance_type?.replace('_', ' ')}</p>
                                            <p className="text-sm text-muted-foreground">{m.description || '-'}</p>
                                            {m.performed_by && (
                                                <p className="text-xs text-muted-foreground mt-1">By: {m.performed_by}</p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <span className="text-sm text-muted-foreground">
                                                {formatDate(m.performed_date)}
                                            </span>
                                            {m.cost && (
                                                <p className="text-sm font-medium">{formatIndianCurrency(m.cost)}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default AssetDetails;
